import { useState, useMemo } from 'react';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Button from '@liquidweb-dev/undercurrent/Button';
import KeyItem from '@liquidweb-dev/undercurrent/KeyItem';
import Counter from '@liquidweb-dev/undercurrent/Counter';
import Trash2Icon from '@liquidweb-dev/undercurrent/icons/lucide/Trash2';
import InfoIcon from '@liquidweb-dev/undercurrent/icons/lucide/Info';
import toast from '@liquidweb-dev/undercurrent/toast';
import useCloudBackupList from 'modules/queries/cloud/backup/useList';
import useServerUpdate from 'modules/queries/server/useUpdate';
import { AssetDetails } from 'modules/queries/asset/useDetails';
import CloudBackupSettingsContent from 'pages/servers/Backups/BackupsBlock/Settings/Cloud/CloudBackupSettingsContent';
import { BackupOption } from 'modules/backups/helpers';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import useProductFlatDetails from 'modules/queries/product/useFlatDetails';
import { useAssetBackupsUpsell } from 'modules/upsell/useUpsellState';
import useSendHubspotEvent from 'utility/gtm/hubspot/useEvent';
import { useBackupsDrawerContext } from 'components/ServersList/BackupsChip/BackupsDrawerContext';

interface CloudDailyBackupSettingsProps {
	assetDetails: AssetDetails;
	backupType: string;
	onBackupTypeChange: (value: string) => void;
	backupOptions?: BackupOption[];
	onDestroy: () => void;
	onClose: () => void;
}

const CloudDailyBackupSettings = ({
	assetDetails,
	backupType,
	onBackupTypeChange,
	backupOptions,
	onDestroy,
	onClose,
}: CloudDailyBackupSettingsProps) => {
	const [backupDays, setBackupDays] = useState(
		assetDetails?.featureDetails?.BackupDay?.num_units || 7,
	);

	const { data: backups, isLoading: isBackupsLoading } = useCloudBackupList({
		uniq_id: assetDetails.uniq_id,
		page_size: 1,
	});

	const { mutate: updateServer, isLoading: updateServerIsLoading } =
		useServerUpdate();

	const { data: productDetails, isLoading: isProductDetailsLoading } =
		useProductFlatDetails(
			{
				product_code: assetDetails?.type,
			},
			{ enabled: Boolean(assetDetails?.type) },
		);

	const [_, setUpsellDismissed] = useAssetBackupsUpsell(assetDetails.uniq_id);

	const sendAddonEvent = useSendHubspotEvent('addon');
	const { onSuccess } = useBackupsDrawerContext();

	const isDestroyScheduled = Boolean(assetDetails?.scheduledCancellationDate);

	const destinationPtovId = useMemo(() => {
		const currentBackupType = assetDetails.backupPlan;
		if (currentBackupType === 'Daily' || currentBackupType === 'CloudDaily') {
			return assetDetails?.featureDetails?.LiquidWebBackupPlan?.ptov_id;
		}

		return productDetails?.flat_options?.find(
			(item) => item?.key === 'BackupDay',
		)?.parent_ptov_id;
	}, [assetDetails, productDetails]);

	const currentDailyBackup = useMemo(
		() =>
			Object.values(assetDetails?.featureDetails || {})?.find(
				(item) => item?.parent_ptov_id === destinationPtovId,
			),
		[assetDetails, destinationPtovId],
	);

	const isEdit = assetDetails?.backupPlan !== 'None';

	const currentBackupOption = backupOptions?.find(
		(opt) => opt.value === backupType,
	);

	const sendEvent = () => {
		if (!isEdit) {
			sendAddonEvent({
				serverUniqId: assetDetails.uniq_id,
				serverHostName: assetDetails.domain,
				addOnName: backupType,
			});
		}
	};

	const handleSubmit = () => {
		updateServer(
			{
				uniq_id: assetDetails.uniq_id,
				features: {
					LiquidWebBackupPlan: backupType,
					BackupDay: { value: 1, num_units: backupDays },
				},
			},
			{
				onSuccess: () => {
					toast.info('Successfully submitted');
					sendEvent();
					setUpsellDismissed(true);
					onClose();
					onSuccess?.();
				},
			},
		);
	};

	return (
		<CloudBackupSettingsContent
			backupType={backupType}
			onBackupTypeChange={onBackupTypeChange}
			isLoading={
				isBackupsLoading || updateServerIsLoading || isProductDetailsLoading
			}
			backupOptions={backupOptions}
			onClose={onClose}
			footerContents={
				<>
					<Button
						disabled={
							currentDailyBackup &&
							assetDetails.instance?.backup_quota === backupDays
						}
						loading={updateServerIsLoading}
						onClick={handleSubmit}
						size="large"
						color={isEdit ? 'primary' : 'highlight'}
					>
						{isEdit ? 'Update' : 'Buy now'}
					</Button>
					{isEdit ? (
						<Button
							variant="secondary"
							color="danger"
							disabled={
								isBackupsLoading ||
								updateServerIsLoading ||
								isProductDetailsLoading ||
								isDestroyScheduled
							}
							size="large"
							startIcon={
								<Trash2Icon
									sx={({ size }) => ({
										width: size.xS,
										height: size.xS,
									})}
								/>
							}
							sx={{ paddingY: 100, paddingX: 200, alignSelf: 'stretch' }}
							onClick={onDestroy}
						>
							Remove backups
						</Button>
					) : (
						<Button
							variant="ghost"
							onClick={onClose}
							disabled={updateServerIsLoading}
							size="large"
						>
							Cancel
						</Button>
					)}
				</>
			}
		>
			<>
				{isEdit && (
					<Stack direction="row" spacing={100}>
						{(backups?.item_total || 0) >= 0 && (
							<KeyItem
								label="Backup Count"
								value={
									isBackupsLoading ? (
										<Skeleton width={100} />
									) : (
										backups?.item_total
									)
								}
								sx={{ flex: 1 }}
							/>
						)}
						{assetDetails.instance?.backup_size && (
							<KeyItem
								label="Current space used"
								value={`${parseFloat(assetDetails?.instance?.backup_size)} GB`}
								sx={{ flex: 1 }}
							/>
						)}
					</Stack>
				)}
				<Stack
					sx={{
						gap: 300,
					}}
				>
					<Stack
						sx={{
							gap: 100,
						}}
					>
						<Counter
							value={backupDays}
							onChange={(value) => setBackupDays(value)}
							label="Number of daily backups to retain (90 max)"
							disabled={updateServerIsLoading}
							min={1}
							max={90}
						/>
						{currentBackupOption && (
							<Typography variant="label-sm">
								${currentBackupOption?.monthlyPrice} per GB monthly
							</Typography>
						)}
					</Stack>
					<Stack
						sx={{
							paddingY: 100,
							paddingX: 200,
							borderRadius: ({ borderRadius }) => borderRadius.roundedSm,
							border: ({ vars }) => `1px solid ${vars.palette.uc.border.weak}`,
							flex: '1 0 0',
							gap: 150,
							flexDirection: 'row',
							alignItems: 'center',
						}}
					>
						<InfoIcon
							sx={({ size }) => ({
								width: size.xS,
								height: size.xS,
							})}
						/>
						<Typography variant="body-sm">
							We recommend at least 7 days of backups
						</Typography>
					</Stack>
				</Stack>
			</>
		</CloudBackupSettingsContent>
	);
};

export default CloudDailyBackupSettings;
